import { CliError, EXIT_INPUT } from "../../../common.js";
import {
  isSupportedScriptVersion,
  requireScriptDocument,
  scriptVersionOf,
  type ScriptDocument,
  type ScriptVersion,
} from "../dispatch.js";
import { isDict } from "../shared.js";
import { migrate as migrateV3ToV4 } from "./v3-to-v4.js";
import type {
  ScriptMigration,
  ScriptMigrationResult,
  ScriptMigrationStepArtifact,
} from "./types.js";

interface RegisteredScriptMigration {
  readonly sourceVersion: ScriptVersion;
  readonly targetVersion: ScriptVersion;
  readonly migrate: ScriptMigration;
}

const MIGRATIONS: readonly RegisteredScriptMigration[] = [
  {
    sourceVersion: 3,
    targetVersion: 4,
    migrate: migrateV3ToV4,
  },
];

function isMigrationArtifact(value: unknown): value is {
  readonly source_version: ScriptVersion;
  readonly target_version: ScriptVersion;
  readonly identity_map: unknown;
} {
  return isDict(value)
    && isSupportedScriptVersion(value["source_version"])
    && isSupportedScriptVersion(value["target_version"])
    && value["source_version"] < value["target_version"]
    && "identity_map" in value;
}

export function parseScriptMigrationStepArtifacts(
  value: unknown,
): ScriptMigrationStepArtifact[] {
  if (!Array.isArray(value)) {
    throw new CliError("Migration manifest steps must be an array.", {
      exitCode: EXIT_INPUT,
      errorCode: "MIGRATE_MANIFEST_INVALID",
    });
  }
  return value.map((item) => {
    if (!isMigrationArtifact(item)) {
      throw new CliError("Migration manifest contains an invalid step artifact.", {
        exitCode: EXIT_INPUT,
        errorCode: "MIGRATE_MANIFEST_INVALID",
      });
    }
    return {
      sourceVersion: item.source_version,
      targetVersion: item.target_version,
      identityMap: item.identity_map,
    };
  });
}

function migrationFor(
  sourceVersion: ScriptVersion,
  targetVersion: ScriptVersion,
): RegisteredScriptMigration | undefined {
  return MIGRATIONS.find((migration) => (
    migration.sourceVersion === sourceVersion
    && migration.targetVersion <= targetVersion
  ));
}

function priorArtifactFor(
  artifacts: readonly ScriptMigrationStepArtifact[],
  migration: RegisteredScriptMigration,
): ScriptMigrationStepArtifact | undefined {
  return artifacts.find((artifact) => (
    artifact.sourceVersion === migration.sourceVersion
    && artifact.targetVersion === migration.targetVersion
  ));
}

export function migrateScriptToVersion(
  value: unknown,
  targetVersion: ScriptVersion,
  priorArtifacts: readonly ScriptMigrationStepArtifact[] = [],
): ScriptMigrationResult {
  const sourceVersion = scriptVersionOf(value);
  if (targetVersion < sourceVersion) {
    throw new CliError("A migration target must be newer than the source script.", {
      exitCode: EXIT_INPUT,
      errorCode: "MIGRATE_TARGET_OLDER",
      data: { expected: [String(sourceVersion)], actual: [String(targetVersion)] },
    });
  }

  let current: ScriptDocument = requireScriptDocument(value);
  let currentVersion = sourceVersion;
  const steps: ScriptMigrationStepArtifact[] = [];
  while (currentVersion < targetVersion) {
    const migration = migrationFor(currentVersion, targetVersion);
    if (migration === undefined) {
      throw new CliError(`No migration path exists from Script v${currentVersion} to Script v${targetVersion}.`, {
        exitCode: EXIT_INPUT,
        errorCode: "MIGRATE_PATH_UNSUPPORTED",
        data: { actual: [String(currentVersion), String(targetVersion)] },
      });
    }
    const migrated = migration.migrate(current, priorArtifactFor(priorArtifacts, migration));
    current = migrated.script;
    currentVersion = migration.targetVersion;
    steps.push(migrated.artifact);
  }

  return { sourceVersion, targetVersion, script: current, steps };
}
